/**
 * `NEEDS_HELP` is set by two-way-communication while a message in the conversation is flagged as
 * needing a human. It masks the real workflow status, which the API reports separately as
 * `activeFlowStatus`.
 *
 * This reads straight through to that underlying status, which is what every *workflow* decision
 * needs: it keeps resolve and unresolve working on a flagged conversation. Mapping to a constant
 * would peg the resolve banner to one state, so the toggle would appear to do nothing.
 *
 * Left unmapped entirely, `NEEDS_HELP` falls through every status check in the app and reads as
 * *closed*, which hides the conversation from the texting indicators.
 *
 * For what to DISPLAY, use `conversationIndicatorStatus` instead — the flag is its own indicator
 * and outranks the workflow status underneath it.
 *
 * @param {?{conversationStatus?: string, activeFlowStatus?: string}} conversation
 * @returns {?string} The status the UI should reason about, or undefined when absent
 */
export const effectiveConversationStatus = (conversation) => {
  const { conversationStatus, activeFlowStatus } = conversation ?? {};

  if (conversationStatus !== 'NEEDS_HELP') return conversationStatus;

  // A flagged conversation always carries a shadow, but fall back to UNRESOLVED rather than
  // letting a missing one read as closed.
  return activeFlowStatus ?? 'UNRESOLVED';
};

/**
 * String-only form, for the places that receive a bare status rather than a conversation. Used
 * as a defensive fallback so a stray `NEEDS_HELP` can never read as resolved.
 *
 * @param {?string} status - Raw conversationStatus from the API
 * @returns {?string} The status the UI should reason about, or undefined when absent
 */
export const normalizeConversationStatus = (status) =>
  status === 'NEEDS_HELP' ? 'UNRESOLVED' : status;

/**
 * Whether Leslie still has a message outstanding on this conversation.
 *
 * Two signals, either of which is enough. `conversationStatus` is the one two-way-communication
 * maintains, and `needsHelpMessageIds` is derived from the messages themselves — which is the
 * source of truth for what is flagged. They are written atomically, but the service documents a
 * cross-collection tear it can only repair on the next transition, and a direct database write
 * skips the guard that keeps them together entirely.
 *
 * Reading only the status meant a conversation with a flagged message showing as new or
 * unresolved, with no way back except flagging another message. Reading either keeps the
 * indicator up for as long as anything says a flag is open, which is the side to fail on: the
 * card asks that it survive until the flag is resolved.
 *
 * @param {?{conversationStatus?: string, needsHelpMessageIds?: Array}} conversation
 * @returns {boolean}
 */
export const conversationHasOpenFlag = (conversation) =>
  conversation?.conversationStatus === 'NEEDS_HELP' ||
  (conversation?.needsHelpMessageIds?.length ?? 0) > 0;

/**
 * The single texting indicator a conversation should display, applying the precedence
 * Leslie Needs Help > New Message > Unresolved Message.
 *
 * Exactly one is returned, which is what makes the precedence hold rather than merely rank:
 * while Leslie's flag is outstanding the conversation is not also counted as new or unresolved,
 * so no later message and no unresolved state can displace the indicator.
 *
 * `leslieEnabled` is the client-side `leslie` flag. With it off this is identical to
 * `effectiveConversationStatus`, so none of the indicators this introduces are displayed.
 *
 * @param {?{conversationStatus?: string, activeFlowStatus?: string}} conversation
 * @param {boolean} [leslieEnabled] - Whether the `leslie` feature flag is on
 * @returns {?string} NEEDS_HELP, or the status the conversation would otherwise show
 */
export const conversationIndicatorStatus = (conversation, leslieEnabled) =>
  leslieEnabled && conversationHasOpenFlag(conversation)
    ? 'NEEDS_HELP'
    : effectiveConversationStatus(conversation);

export const groupByKey = (array, keyFn) => {
  return array.reduce((acc, item) => {
    const key = keyFn(item);
    if (key == null) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

/**
 * Calculates the texting status metrics for a set of recipient IDs
 * @param {Array} recipientIds - List of recipient IDs
 * @param {Object} conversationsDictionary - Grouped dictionary of conversations by customer ID (values are arrays)
 * @param {boolean} [leslieEnabled] - Whether the `leslie` feature flag is on. Defaults to off, so
 *   a caller that does not pass it counts exactly as it did before Leslie existed.
 * @returns {Object} Conversation counts by status
 */
export const calculateTextingStatus = (
  recipientIds,
  conversationsDictionary,
  leslieEnabled = false,
) => {
  const conversationsStatus = {
    NEEDS_HELP: 0,
    NEW: 0,
    UNRESOLVED: 0,
    RESOLVED: 0,
  };

  recipientIds.forEach((customerId) => {
    const conversations = conversationsDictionary[customerId];
    if (!conversations) return;
    const items = Array.isArray(conversations)
      ? conversations
      : [conversations];
    items.forEach((conversation) => {
      const status = conversationIndicatorStatus(conversation, leslieEnabled);
      if (Object.prototype.hasOwnProperty.call(conversationsStatus, status)) {
        conversationsStatus[status] += 1;
      }
    });
  });

  // TOTAL is what decides whether the row links through to the Texting tab at all, so a
  // household whose only signal is a Leslie flag has to count towards it.
  conversationsStatus.TOTAL =
    conversationsStatus.NEEDS_HELP +
    conversationsStatus.NEW +
    conversationsStatus.UNRESOLVED;

  return conversationsStatus;
};

/**
 * The texting column's text form, used by the CSV and PDF exports.
 *
 * The Leslie Needs Help count is only added when there is one, so an export from a portfolio
 * with nothing flagged is identical to what it was before. It cannot simply be left out: the
 * counts are disjoint, so a household whose only conversation is flagged would otherwise export
 * as "New = 0, Unresolved = 0" and read as having no texting activity at all.
 *
 * @param {?Object} texting - A counts object as produced by calculateTextingStatus
 * @returns {string}
 */
export const formatTextingCounts = (texting) => {
  const { NEEDS_HELP = 0, NEW = 0, UNRESOLVED = 0 } = texting ?? {};
  const counts = `New = ${NEW}, Unresolved = ${UNRESOLVED}`;

  return NEEDS_HELP > 0
    ? `Leslie Needs Help = ${NEEDS_HELP}, ${counts}`
    : counts;
};

export const textingColumnTableFilterFn = (filterByArr, filterValue) => {
  if (!filterValue || typeof filterValue !== 'object') {
    return false;
  }
  if (filterByArr.includes('new') && filterValue.NEW > 0) {
    return true;
  }
  if (filterByArr.includes('unresolved') && filterValue.UNRESOLVED > 0) {
    return true;
  }
  if (filterByArr.includes('needs_help') && filterValue.NEEDS_HELP > 0) {
    return true;
  }

  // `---` means "no texting indicator at all". A Leslie flag is an indicator, and it is
  // counted on its own rather than as NEW or UNRESOLVED, so without this the flagged
  // households would be the ones the empty filter returns.
  if (
    filterByArr.includes('---') &&
    !filterValue.NEEDS_HELP &&
    filterValue.NEW === 0 &&
    filterValue.UNRESOLVED === 0
  ) {
    return true;
  }
  return false;
};
